import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './BrokerAuthExpiredCard.module.css';

export interface BrokerAuthExpiredCardProps {
  /** Optional specific mode ("paper" | "live") the 401 came from — mentioned in the copy. */
  mode?: string;
  /** Error hint from the backend (e.g. `err.reconnectHint`). Falls back to a default. */
  hint?: string;
  /** Layout: "inline" fits in-table; "full" is the empty-state-centered card. */
  variant?: 'inline' | 'full';
  /** Override the default CTA destination. */
  ctaHref?: string;
}

/** Friendly broker-auth-expired card shown wherever a page's Alpaca call
 * returns 401. The backend stamps the response with `broker_auth_expired:
 * true` + `reconnect_hint`; the frontend's fetchApi throws with
 * `err.brokerAuthExpired` set. Pages catch that and render this component. */
const BrokerAuthExpiredCard: React.FC<BrokerAuthExpiredCardProps> = ({
  mode,
  hint,
  variant = 'full',
  ctaHref = '/settings?tab=broker',
}) => {
  const navigate = useNavigate();
  const defaultHint = `Your Alpaca ${mode || 'broker'} credentials have expired or been revoked. Reconnect to restore access to positions, orders, and trading.`;
  return (
    <div className={`${styles.card} ${variant === 'inline' ? styles.inline : styles.full}`}>
      <div className={styles.icon} aria-hidden>
        ⚠
      </div>
      <div className={styles.body}>
        <h3 className={styles.title}>Broker connection expired</h3>
        <p className={styles.copy}>{hint || defaultHint}</p>
      </div>
      <button className={styles.cta} onClick={() => navigate(ctaHref)}>
        Go to Broker Settings
      </button>
    </div>
  );
};

export default BrokerAuthExpiredCard;
